import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import { toast } from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import Header from 'shared/components/Header';
import { useAuth } from 'shared/hooks/useAuth';
import useProfilePhotoSrc from 'shared/hooks/useProfilePhotoSrc';
import { useTenantNavigate } from 'shared/hooks/useTenantNavigate';
import { riskRegisterApi } from '../../api/systemRegisterApi';
import { formatEnumLabel } from '../../constants/enums';
import { apiErrorMessage, unwrapData, unwrapList } from '../../utils/responseHelpers';
import {
  getReportFormPalette as getFormPalette,
  REPORT_PRINT_KEEP_CSS,
  reportHeaderPrintSx,
} from 'shared/utils/reportFormPalette';
import { downloadFormPdf } from 'shared/utils/downloadFormPdf';

const HEADER_BLUE = '#0D2240';
const FORM_PRINT_ROOT_ID = 'risk-register-form';
const PRINTING_BODY_CLASS = 'sr-printing-risk-register-form';

function dash(value) {
  const v = typeof value === 'string' ? value.trim() : value;
  return v || v === 0 ? String(v) : '';
}

const FieldCell = ({ label, children, sx, colSpan, border, muted }) => (
  <TableCell
    colSpan={colSpan}
    sx={{
      border,
      p: 1.25,
      verticalAlign: 'top',
      color: 'inherit',
      ...sx,
    }}
  >
    <Typography
      component="span"
      sx={{ fontWeight: 700, fontSize: '0.78rem', display: 'block', mb: 0.5, color: 'inherit' }}
    >
      {label}
    </Typography>
    <Box sx={{ minHeight: 22, fontSize: '0.9rem', color: 'inherit', whiteSpace: 'pre-wrap' }}>
      {children || <Box sx={{ color: muted, opacity: 0.7 }}>&nbsp;</Box>}
    </Box>
  </TableCell>
);

export default function RiskRegisterViewPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { navigateTenant } = useTenantNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const riskId = searchParams.get('id');

  const [detail, setDetail] = useState(null);
  const [associatedSoftware, setAssociatedSoftware] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const profile = user?.data;
  const tenantBranding = profile?.tenantBranding;
  const form = useMemo(
    () => getFormPalette(isDark, tenantBranding),
    [isDark, tenantBranding?.secondaryColor]
  );
  const tenantLogoSrc = useProfilePhotoSrc(tenantBranding?.tenantPhoto);
  const tenantDisplayName =
    (profile?.tenantName && String(profile.tenantName).trim())
    || (tenantBranding?.name && String(tenantBranding.name).trim())
    || 'Organisation';

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!riskId) {
        setError('No risk selected.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      try {
        const [detailResponse, softwareResponse] = await Promise.all([
          riskRegisterApi.get(riskId),
          riskRegisterApi.listSoftwareByRiskId(riskId),
        ]);
        if (cancelled) return;
        setDetail(unwrapData(detailResponse));
        const softwareRows = unwrapList(softwareResponse);
        setAssociatedSoftware(
          (Array.isArray(softwareRows) ? softwareRows : [])
            .filter((row) => row?.softwareId != null && row?.softwareRiskId != null)
            .map((row) => ({
              ...row,
              id: row.softwareRiskId,
            }))
        );
      } catch (err) {
        if (!cancelled) {
          setError(apiErrorMessage(err, 'Failed to load risk details.'));
          setAssociatedSoftware([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [riskId]);

  const handlePrint = useCallback(() => {
    const previousTitle = document.title;
    document.title = ' ';
    const cleanup = () => {
      document.title = previousTitle;
      document.body.classList.remove(PRINTING_BODY_CLASS);
      window.removeEventListener('afterprint', cleanup);
    };
    document.body.classList.add(PRINTING_BODY_CLASS);
    window.addEventListener('afterprint', cleanup);
    window.print();
    setTimeout(cleanup, 1500);
  }, []);

  const handleDownloadPdf = useCallback(async () => {
    const formEl = document.getElementById(FORM_PRINT_ROOT_ID);
    if (!formEl || !detail || downloadingPdf) return;

    setDownloadingPdf(true);
    try {
      const labelBase = (detail.riskName || 'risk-register')
        .replace(/[^\w\-]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '')
        .slice(0, 48) || 'risk-register';
      await downloadFormPdf({
        element: formEl,
        fileName: `${labelBase}-RISK-REGISTER-FORM.pdf`,
      });
      toast.success('PDF downloaded.');
    } catch (error) {
      console.error(error);
      toast.error('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  }, [detail, downloadingPdf]);

  useEffect(() => {
    const style = document.createElement('style');
    style.setAttribute('data-sr-risk-register-form-print', 'true');
    style.textContent = `
      @page { size: A4; margin: 0; }
      @media print {
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          background: #fff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        body.${PRINTING_BODY_CLASS} .no-print { display: none !important; }
        body.${PRINTING_BODY_CLASS} * { visibility: hidden !important; }
        body.${PRINTING_BODY_CLASS} #${FORM_PRINT_ROOT_ID},
        body.${PRINTING_BODY_CLASS} #${FORM_PRINT_ROOT_ID} * {
          visibility: visible !important;
        }
        body.${PRINTING_BODY_CLASS} #${FORM_PRINT_ROOT_ID} {
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          width: 100% !important;
          margin: 0 !important;
          box-shadow: none !important;
        }
        ${REPORT_PRINT_KEEP_CSS}
      }
    `;
    document.head.appendChild(style);
    return () => {
      style.remove();
      document.body.classList.remove(PRINTING_BODY_CLASS);
    };
  }, []);

  if (loading) {
    return (
      <Box m={{ xs: '8px', sm: '12px' }} display="flex" justifyContent="center" py={8}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  if (!detail) {
    return (
      <Box m={{ xs: '8px', sm: '12px' }}>
        <Header title="Risk Register Form" />
        <Paper variant="outlined" sx={{ p: 3, mt: 2, borderRadius: 2 }}>
          <Typography variant="body1" mb={2}>
            {error || 'Open a risk from the Risk Register using the view action.'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigateTenant('/system-register/risk-register')}
          >
            Go back
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      m={{ xs: '8px', sm: '12px' }}
      sx={{
        height: 'calc(100vh - 96px)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        '@media print': {
          height: 'auto',
          m: 0,
          bgcolor: '#fff',
          '& .no-print': { display: 'none !important' },
        },
      }}
    >
      <Box className="no-print">
        <Header title="Risk Register Form" subtitle={detail.riskName || ''} />
      </Box>

      <Box
        className="no-print"
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        mb={2}
      >
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigateTenant('/system-register/risk-register')}
        >
          Back
        </Button>
        <Button type="button" startIcon={<PrintOutlinedIcon />} onClick={handlePrint} variant="outlined">
          Print
        </Button>
        <Button
          type="button"
          startIcon={
            downloadingPdf ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfOutlinedIcon />
          }
          onClick={handleDownloadPdf}
          variant="contained"
          color="secondary"
          disabled={downloadingPdf}
        >
          {downloadingPdf ? 'Preparing PDF…' : 'Download PDF'}
        </Button>
      </Box>

      <Paper
        id={FORM_PRINT_ROOT_ID}
        elevation={0}
        sx={{
          overflow: 'auto',
          bgcolor: form.surface,
          color: form.text,
          borderRadius: 0,
          border: form.border,
          maxWidth: 980,
          mx: 'auto',
          width: '100%',
          boxShadow: isDark
            ? `0 0 0 1px ${alpha('#fff', 0.04)}, 0 8px 28px ${alpha('#000', 0.45)}`
            : 'none',
          '@media print': {
            border: 'none',
            maxWidth: '100%',
            overflow: 'visible',
            bgcolor: '#fff !important',
            color: '#111 !important',
            boxShadow: 'none',
          },
        }}
      >
        <Box
          sx={{
            bgcolor: form.banner,
            color: form.bannerText,
            px: { xs: 1.5, sm: 2.5 },
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
              '@media print': reportHeaderPrintSx,
          }}
        >
          <Box
            sx={{
              width: { xs: 108, sm: 148 },
              height: { xs: 72, sm: 96 },
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: form.logoBg,
              overflow: 'hidden',
              p: 0,
              border: isDark ? `1px solid ${alpha('#fff', 0.2)}` : 'none',
            }}
          >
            {tenantLogoSrc ? (
              <Box
                component="img"
                src={tenantLogoSrc}
                alt={`${tenantDisplayName} logo`}
                sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            ) : (
              <Typography sx={{ color: HEADER_BLUE, fontWeight: 800, fontSize: '0.7rem', textAlign: 'center' }}>
                LOGO
              </Typography>
            )}
          </Box>
          <Box flex={1} minWidth={140}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.15rem', sm: '1.5rem' },
                letterSpacing: 0.2,
                lineHeight: 1.25,
                color: form.bannerText,
              }}
            >
              {tenantDisplayName}
            </Typography>
          </Box>
          <Box textAlign={{ xs: 'left', sm: 'right' }} flexShrink={0}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '0.95rem', sm: '1.15rem' },
                letterSpacing: 0.4,
                lineHeight: 1.25,
                color: form.bannerText,
              }}
            >
              Risk Register Form
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', mt: 0.35, opacity: 0.92, color: form.bannerText }}>
              SR RR1
            </Typography>
          </Box>
        </Box>

        <Table size="small" sx={{ tableLayout: 'fixed', color: 'inherit' }}>
          <TableBody>
            <TableRow>
              <FieldCell label="Risk name:" border={form.border} muted={form.muted} sx={{ width: '40%' }}>
                {dash(detail.riskName) || '—'}
              </FieldCell>
              <FieldCell label="Risk category:" border={form.border} muted={form.muted} sx={{ width: '30%' }}>
                {formatEnumLabel(detail.riskCategory) || dash(detail.riskCategory) || '—'}
              </FieldCell>
              <FieldCell label="Risk priority:" border={form.border} muted={form.muted} sx={{ width: '30%' }}>
                {formatEnumLabel(detail.riskPriority) || '—'}
              </FieldCell>
            </TableRow>
            <TableRow>
              <FieldCell label="Resources impacted:" border={form.border} muted={form.muted}>
                {dash(detail.resourcesImpacted) || '—'}
              </FieldCell>
              <FieldCell label="Risk owner:" border={form.border} muted={form.muted}>
                {dash(detail.riskOwner) || '—'}
              </FieldCell>
              <FieldCell label="Status:" border={form.border} muted={form.muted}>
                {formatEnumLabel(detail.status) || '—'}
              </FieldCell>
            </TableRow>
            <TableRow>
              <FieldCell label="Risk description:" border={form.border} muted={form.muted} colSpan={3}>
                {dash(detail.riskDescription) || '—'}
              </FieldCell>
            </TableRow>
            <TableRow>
              <FieldCell label="Impact of the risk:" border={form.border} muted={form.muted} colSpan={3}>
                {dash(detail.impact) || '—'}
              </FieldCell>
            </TableRow>
            <TableRow>
              <FieldCell label="Causes of the risk:" border={form.border} muted={form.muted} colSpan={3}>
                {dash(detail.causes) || '—'}
              </FieldCell>
            </TableRow>
            <TableRow>
              <FieldCell label="Controls and mitigation:" border={form.border} muted={form.muted} colSpan={3}>
                {dash(detail.controlsAndMitigation) || '—'}
              </FieldCell>
            </TableRow>
          </TableBody>
        </Table>

        <Box
          sx={{
            px: 1.5,
            py: 1,
            bgcolor: form.headBg,
            borderTop: form.border,
            borderBottom: form.border,
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', letterSpacing: 0.4, color: 'inherit' }}>
            Associated Software
          </Typography>
        </Box>

        <Table size="small" sx={{ tableLayout: 'fixed', color: 'inherit' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ border: form.border, fontWeight: 700, width: '50%' }}>
                Software / system name
              </TableCell>
              <TableCell sx={{ border: form.border, fontWeight: 700, width: '30%' }}>Internal ID</TableCell>
              <TableCell sx={{ border: form.border, fontWeight: 700, width: '20%' }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {associatedSoftware.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} sx={{ border: form.border, color: form.muted }}>
                  This risk is not linked to any software yet.
                </TableCell>
              </TableRow>
            ) : (
              associatedSoftware.map((row) => (
                <TableRow key={row.softwareRiskId}>
                  <TableCell sx={{ border: form.border }}>{row.softwareName || '—'}</TableCell>
                  <TableCell sx={{ border: form.border }}>{row.internalReference || '—'}</TableCell>
                  <TableCell sx={{ border: form.border }}>
                    {formatEnumLabel(row.softwareStatus) || row.softwareStatus || '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <Box
          sx={{
            borderTop: form.border,
            py: 1.25,
            px: 1.5,
            textAlign: 'center',
            fontSize: '0.75rem',
            color: form.muted,
            '@media print': { color: '#333 !important', borderTop: '1px solid #1a1a1a !important' },
          }}
        >
          Please retain this form for audit and compliance.
        </Box>
      </Paper>
    </Box>
  );
}
